import { useEffect, useState } from 'react';
import ArrowLeftImage from '../../../assets/icons/arrow_left.png';
import forestStageBackground from '../../../assets/cat-running/backgrounds/forest-bg.png';
import magnetIcon from '../../../assets/cat-running/items/magnet.png';
import shieldIcon from '../../../assets/cat-running/items/shield.png';
import heartCounterIcon from '../../../assets/cat-running/ui/icon-heart.png';
import snackCounterIcon from '../../../assets/cat-running/ui/icon-snack-counter.png';
import snackBowlIcon from '../../../assets/icons/mulnyang/snack-bowl.png';
import CatRunningOutlineFrame from './CatRunningOutlineFrame';
import CatRunningReadyItemFrame from './CatRunningReadyItemFrame';
import { CAT_RUNNER_PRE_ITEM_TYPES } from '../mockBackend';
import { preloadSharedCatRunningAssets } from '../game/assets';

const CAT_RUNNING_ITEMS_READY = [
  {
    itemType: CAT_RUNNER_PRE_ITEM_TYPES.MAGNET,
    label: '자석',
    description: ['츄르가 저절로 붙는다!'],
    icon: magnetIcon,
  },
  {
    itemType: CAT_RUNNER_PRE_ITEM_TYPES.SHIELD,
    label: '무적방패',
    description: ['모든 장애물 무시'],
    icon: shieldIcon,
  },
];

function CatRunningStatusFrame({ icon, label, value, iconClassName }) {
  return (
    <CatRunningOutlineFrame
      className="h-10"
      contentClassName="flex items-center justify-between px-6"
      fillClassName="bg-black/40"
      contentAbsolute={true}
    >
      <div className="flex items-center gap-2">
        <img src={icon} alt="" className={iconClassName} />
        <span className="font-['Pretendard'] text-[14px] font-bold text-white">{label}</span>
      </div>
      <span className="font-['Pretendard'] text-[14px] font-bold text-white">{value}</span>
    </CatRunningOutlineFrame>
  );
}

export default function CatRunningReadyScreen({
  onBack,
  onHome,
  onStart,
  onOpenGuide,
  armedPreItemTypes = [],
  onArmPreItem,
  isRoundResetting = false,
  isRoundResetReady = false,
  snackCount,
  heartsRemaining,
  adLoadingItemType = null,
  snackShortageOpen = false,
  snackChargeLoading = false,
  onCloseSnackShortage,
  onChargeSnack,
}) {
  const freeHeartCountText = `${heartsRemaining}`;
  const snackCountText = `${snackCount}`;
  const [isStartingGame, setIsStartingGame] = useState(false);
  const isStartDisabled = isStartingGame || isRoundResetting || !isRoundResetReady;
  const isAnyAdLoading = Boolean(adLoadingItemType);

  useEffect(() => {
    preloadSharedCatRunningAssets();
  }, []);

  const handleStartClick = async () => {
    if (isStartDisabled) return;
    setIsStartingGame(true);
    try {
      await preloadSharedCatRunningAssets();
    } catch (error) {
    }
    try {
      await onStart?.();
    } finally {
      setIsStartingGame(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0B0B]">
      <div className="mx-auto flex min-h-screen w-full max-w-[500px] justify-center">
        <div className="relative min-h-screen w-full overflow-hidden bg-[#111111] pb-[118px]">
          <div className="absolute inset-0">
            <img src={forestStageBackground} alt="준비 배경" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-[rgba(0,0,0,0.32)]" />
          </div>

          <div className="absolute inset-x-0 top-0 z-20 flex h-11 items-center justify-between px-3">
            <button
              type="button"
              className="relative z-20 flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0"
              onClick={onBack}
              aria-label="뒤로가기"
            >
              <img src={ArrowLeftImage} alt="" className="h-6 w-6 object-contain brightness-0 invert" />
            </button>
            <button
              type="button"
              onClick={onOpenGuide}
              aria-label="이용 안내 열기"
              className="border-0 bg-transparent p-0 text-center font-['Pretendard'] text-sm font-bold leading-[21px] text-white"
            >
              안내
            </button>
          </div>

          <div className="relative z-10 px-[10px] pb-10 pt-[106px]">
            <h2 className="m-0 text-center font-['DungGeunMo'] text-[40px] font-light tracking-[-1.2px] text-white [text-shadow:3px_3px_0_rgba(0,0,0,0.65)]">
              출발준비
            </h2>

            <div className="mt-[92px] grid grid-cols-2 gap-[clamp(10px,2.5vw,16px)]">
              <CatRunningStatusFrame
                icon={heartCounterIcon}
                label="무료하트"
                value={freeHeartCountText}
                iconClassName="h-4 w-4 object-contain"
              />
              <CatRunningStatusFrame
                icon={snackCounterIcon}
                label="간식"
                value={snackCountText}
                iconClassName="h-4 w-[18px] object-contain"
              />
            </div>

            <div className="mt-[22px] grid grid-cols-2 gap-[10px]">
              {CAT_RUNNING_ITEMS_READY.map((item) => {
                const isArmed = armedPreItemTypes.includes(item.itemType);
                const isLoading = adLoadingItemType === item.itemType;
                const isItemDisabled = isArmed || isAnyAdLoading;

                return (
                  <button
                    key={item.itemType}
                    type="button"
                    className={`border-0 bg-transparent p-0 text-left ${
                      isArmed
                        ? 'cursor-not-allowed opacity-40 grayscale'
                        : isLoading
                          ? 'cursor-wait opacity-90'
                          : isAnyAdLoading
                            ? 'cursor-not-allowed opacity-60'
                            : ''
                    }`}
                    onClick={() => onArmPreItem?.(item.itemType)}
                    disabled={isItemDisabled}
                    aria-busy={isLoading}
                  >
                    <CatRunningReadyItemFrame isSelected={isArmed}>
                      <article className="flex flex-col items-center justify-start">
                        <img src={item.icon} alt="" className="h-10 w-10 object-contain" />
                        <h3 className="m-0 mt-2 font-['Pretendard'] text-[18px] font-bold text-[#F4F4F4]">
                          {item.label}
                        </h3>
                        <p className="m-0 mt-4 text-center font-['Pretendard'] text-sm leading-[1.4] text-[#D9D9D9]">
                          {isLoading ? (
                            '광고 로딩중'
                          ) : (
                            <>
                              {item.description[0]}
                              <br />
                              {item.description[1]}
                            </>
                          )}
                        </p>
                      </article>
                    </CatRunningReadyItemFrame>
                  </button>
                );
              })}
            </div>

            <CatRunningOutlineFrame
              className="mt-7"
              contentClassName="flex flex-col justify-center px-3 !py-[10px] text-center ml-4 font-['Pretendard'] text-[14px] leading-[1.4] text-white"
              fillClassName="bg-[rgba(0,0,0,0.4)]"
            >
              <p className="m-0">- 아이템을 획득하고 플레이하세요!
              </p>
              <p className="m-0">- 아이템 선택 시 광고가 재생됩니다
              </p>
            </CatRunningOutlineFrame>

            <div className="mt-9 flex flex-col gap-[10px]">
              <button
                type="button"
                className={`flex h-[60px] items-center justify-center gap-2 rounded-[8px] border-0 bg-[#3B5CD2] font-['Pretendard'] text-[16px] font-bold text-white ${
                  isStartingGame ? 'cursor-wait opacity-80' : ''
                }`}
                onClick={handleStartClick}
                disabled={isStartDisabled}
                aria-label="게임시작"
              >            
                <span>{isStartingGame ? '게임 로딩중' : '게임시작'}</span>
              </button>
              <button
                type="button"
                className="h-[60px] rounded-[8px] border-0 bg-black font-['Pretendard'] text-[16px] font-bold text-white"
                onClick={onHome}
              >
                홈으로
              </button>
            </div>
          </div>
        </div>
        {snackShortageOpen ? (
          <div
            className="fixed inset-0 z-[100] flex min-h-[100dvh] w-screen items-center justify-center bg-[rgba(0,0,0,0.58)] p-6 backdrop-blur-[12px]"
            role="dialog"
            aria-modal="true"
            aria-label="간식 부족 팝업"
          >
            <div className="relative z-[1] w-full max-w-[320px] rounded-[16px] bg-[linear-gradient(180deg,#dfd7ff_0%,#ffffff_85%)] px-[10px] pb-[10px] pt-[30px] text-center shadow-[0_5px_16px_rgba(0,0,0,0.1)]">
              <button
                type="button"
                className="absolute right-4 top-4 inline-flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 text-[34px] font-light leading-[20px] text-[#333]"
                aria-label="팝업 닫기"
                onClick={onCloseSnackShortage}
                disabled={snackChargeLoading}
              >
                ×
              </button>
              <img src={snackBowlIcon} alt="" className="mx-auto block h-[63px] w-[70px] object-contain" />
              <h3 className="m-0 mt-5 font-['Pretendard'] text-[22px] font-extrabold leading-[1.4] tracking-[0.44px] text-[#333]">간식 부족</h3>
              <p className="m-0 mt-2 font-['Pretendard'] text-[14px] font-bold leading-[1.4] tracking-[0.28px] text-[#8b68dd]">
                {`보유간식 : ${Number(snackCount).toLocaleString()}개`}
              </p>
              <p className="m-0 mt-[5px] font-['Pretendard'] text-[14px] font-bold leading-[1.4] tracking-[0.28px] text-[#5e5e5e]">
                <span className="text-[#f6475f]">간식 1개</span>가 부족해요
              </p>
              <button
                type="button"
                className="mt-[30px] flex h-[52px] w-full items-center justify-center rounded-[8px] border-0 bg-[#1e1e1e] font-['Pretendard'] text-[16px] font-bold leading-[1.4] tracking-[-0.3px] text-white disabled:cursor-not-allowed disabled:bg-[#d9d9d9] disabled:text-[#8a8a8a]"
                onClick={onChargeSnack}
                disabled={snackChargeLoading}
              >
                {snackChargeLoading ? '로딩 중' : '충전하러 가기'}
              </button>
              <button
                type="button"
                className="mt-[10px] h-[52px] w-full rounded-[8px] border-0 bg-[#f4f4f4] font-['Pretendard'] text-[16px] font-bold leading-[1.4] tracking-[-0.3px] text-[#919191] disabled:cursor-not-allowed disabled:bg-[#d9d9d9] disabled:text-[#8a8a8a]"
                onClick={onHome}
                disabled={snackChargeLoading}
              >
                로비로 돌아가기
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
